/**
 * Outlook slash-command UX extras — auto-send predicates only.
 *
 * The user-facing slash list (slash sigil, skillKey, description, hint) is
 * fetched from the backend (`GET /api/v1/skills/slash-commands?channel=outlook`)
 * — see ``src/chat/slashCommands/registry.ts``. Adding or renaming a slash
 * is a Cosmos change, not a frontend code change.
 *
 * This file owns only what can't live in JSON: per-command behaviour
 * functions (e.g. ``/catchup`` always auto-sends; ``/reply`` and
 * ``/summarise`` carry a stock fallback message for the empty-args Enter
 * case). The registry merges these into each command at load time.
 */
import type { SlashCommandExtras } from "../../../src/chat/slashCommands/types";

// Must be BYTE-IDENTICAL to the general_outlook "Catch up" pill message so the
// slash and the pill send the same turn and return the same result (AIVA-5348).
// No "today" — the window is enforced by the backend (CATCHUP_UNREAD_WINDOW_DAYS).
const CATCHUP_MESSAGE = "Show me what needs my attention";
const REPLY_OPEN_EMAIL_MESSAGE = "Draft a reply to the email I have open.";
const SUMMARISE_OPEN_EMAIL_MESSAGE =
  "Summarise the email I have open, including any attachments.";

/**
 * Slash → behaviour extras for Outlook. Slashes not present in this map
 * get the default behaviour: prefill on menu click, no auto-send fallback.
 */
export const OUTLOOK_SLASH_EXTRAS: Readonly<Record<string, SlashCommandExtras>> = Object.freeze({
  // Always prefill — the user may want to reply to a different email
  // (e.g. one from a prior search) or add an instruction. The fallback
  // message kicks in when they hit Enter with no args typed; the
  // email_draft skill's Step 0 logic resolves the target.
  "/reply": {
    autoSendMessage: (): string => REPLY_OPEN_EMAIL_MESSAGE,
  },
  // Always prefill — the user may want to summarise a different email or
  // specify a format ("in 3 bullets"). Fallback covers empty-args Enter.
  "/summarise": {
    autoSendMessage: (): string => SUMMARISE_OPEN_EMAIL_MESSAGE,
  },
  // Always auto-sends — there's nothing for the user to add; the catch-up
  // is just "show me what matters now".
  "/catchup": {
    autoSendWhen: (): boolean => true,
    autoSendMessage: (): string => CATCHUP_MESSAGE,
  },
});
